import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock3,
  KeyRound,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import api from "../lib/api";
import { getTenantSlug } from "../lib/tenant";
import Captcha, { resetCaptcha } from "../components/Captcha";
import { MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import MarketingSeo from "../components/marketing/MarketingSeo";
import { Button, Input, Label, Skeleton } from "../components/ui/primitives";
import { BrandCopy } from "../components/BrandWordmarkText";

export const ACCOUNT_DELETION_SESSION_KEY = "vp-account-deletion-reference-v1";
export const RESEND_COOLDOWN_SECONDS = 45;

const PERSONAS = Object.freeze([
  { value: "staff", label: "Staff", hint: "Teacher, administrator or other school staff" },
  { value: "parent", label: "Parent", hint: "Parent or lawful guardian account" },
  { value: "student", label: "Student", hint: "Student account" },
]);

const STATUS_COPY = Object.freeze({
  verification_pending: {
    title: "Verification required",
    description: "Enter the code sent to the account's registered contact.",
    tone: "caution",
  },
  pending_review: {
    title: "Awaiting review",
    description: "Your verified request is waiting for the school and Vidyapeeth360 retention review.",
    tone: "informative",
  },
  processing: {
    title: "Processing",
    description: "The approved request is being processed under the applicable retention requirements.",
    tone: "informative",
  },
  completed: {
    title: "Completed",
    description: "The account-deletion request has been completed.",
    tone: "positive",
  },
  declined: {
    title: "Review completed",
    description: "The request could not be approved after review.",
    tone: "critical",
  },
  cancelled: {
    title: "Cancelled",
    description: "This account-deletion request was cancelled before processing.",
    tone: "neutral",
  },
});

const EMPTY_FORM = Object.freeze({ school_code: "", persona: "", identifier: "" });

function isNonEmptyString(value, maximum = Infinity) {
  return typeof value === "string" && value.trim().length > 0 && value.trim().length <= maximum;
}

export function hasCompleteDeletionPolicy(policy) {
  return Boolean(
    policy
      && isNonEmptyString(policy.policy_version, 200)
      && isNonEmptyString(policy.retention_notice_version, 200)
      && isNonEmptyString(policy.title, 200)
      && isNonEmptyString(policy.summary, 2000)
      && isNonEmptyString(policy.review_notice, 3000)
      && isNonEmptyString(policy.student_and_parent_notice, 3000)
      && Array.isArray(policy.effects_after_approval)
      && policy.effects_after_approval.length > 0
      && policy.effects_after_approval.every((item) => isNonEmptyString(item, 1000))
      && isNonEmptyString(policy.cancellation_notice, 2000)
  );
}

function isValidReference(reference) {
  return Boolean(
    reference
      && isNonEmptyString(reference.school_code, 120)
      && isNonEmptyString(reference.request_id, 64)
      && isNonEmptyString(reference.access_key, 128)
      && (reference.resend_available_at == null || Number.isFinite(reference.resend_available_at))
  );
}

export function readDeletionSession(storage = window.sessionStorage) {
  try {
    const parsed = JSON.parse(storage.getItem(ACCOUNT_DELETION_SESSION_KEY) || "null");
    return isValidReference(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeDeletionSession(reference, storage = window.sessionStorage) {
  if (!isValidReference(reference)) throw new Error("Invalid account-deletion reference.");
  storage.setItem(ACCOUNT_DELETION_SESSION_KEY, JSON.stringify(reference));
}

export function clearDeletionSession(storage = window.sessionStorage) {
  try { storage.removeItem(ACCOUNT_DELETION_SESSION_KEY); } catch { /* private browsing */ }
}

export function resendSecondsRemaining(reference, now = Date.now()) {
  return Math.max(0, Math.ceil(((Number(reference?.resend_available_at) || 0) - now) / 1000));
}

function safeDetail(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail.trim() : fallback;
}

function requestStage(request) {
  return request?.status === "verification_pending" ? "verify" : "status";
}

function loginPath(persona) {
  if (persona === "parent") return "/parent/login";
  if (persona === "student") return "/student/login";
  return "/login";
}

function formatDate(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function Progress({ stage }) {
  const active = stage === "start" ? 0 : stage === "verify" ? 1 : 2;
  const steps = ["Account details", "Verify contact", "Review status"];
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Account-deletion request progress">
      {steps.map((label, index) => {
        const complete = index < active;
        const current = index === active;
        return (
          <li key={label} className="min-w-0" aria-current={current ? "step" : undefined}>
            <div className={`h-1 rounded-full ${index <= active ? "bg-accent" : "bg-line"}`} aria-hidden="true" />
            <span className={`mt-2 block text-[11px] sm:text-xs ${current ? "font-semibold text-ink" : "text-ink-subtle"}`}>
              <span className="sr-only">Step {index + 1}: </span>{complete ? <Check className="mr-1 inline h-3 w-3" aria-hidden="true" /> : null}{label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function PolicyPanel({ policy }) {
  return (
    <section aria-labelledby="deletion-notice-title" className="lg:sticky lg:top-24">
      <div className="inline-grid h-12 w-12 place-items-center rounded-xl bg-critical-soft text-critical">
        <ShieldCheck className="h-6 w-6" aria-hidden="true" />
      </div>
      <h1 id="deletion-notice-title" className="mt-5 max-w-xl font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
        {policy.title}
      </h1>
      <p className="mt-4 max-w-2xl text-base leading-7 text-ink-2">{policy.summary}</p>

      <div className="mt-7 border-y border-line py-6">
        <h2 className="font-heading text-base font-semibold text-ink">What happens after approval</h2>
        <ul className="mt-3 space-y-3">
          {policy.effects_after_approval.map((effect) => (
            <li key={effect} className="flex gap-3 text-sm leading-6 text-ink-2">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-positive" aria-hidden="true" />
              <span>{effect}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 space-y-4 text-sm leading-6 text-ink-2">
        <p>{policy.review_notice}</p>
        <p>{policy.student_and_parent_notice}</p>
        <p>{policy.cancellation_notice}</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm">
        <Link to="/privacy-policy" className="font-medium text-accent-active underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/50">Privacy Policy</Link>
        <Link to="/terms-and-conditions" className="font-medium text-accent-active underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/50">Terms &amp; Conditions</Link>
      </div>
      <dl className="mt-6 space-y-1 text-xs text-ink-subtle">
        <div className="flex flex-wrap gap-x-2"><dt>Deletion notice:</dt><dd className="break-all font-medium text-ink-2">{policy.policy_version}</dd></div>
        <div className="flex flex-wrap gap-x-2"><dt>Retention notice:</dt><dd className="break-all font-medium text-ink-2">{policy.retention_notice_version}</dd></div>
      </dl>
    </section>
  );
}

function PolicyLoading() {
  return (
    <main className="mx-auto grid w-full max-w-6xl flex-1 gap-10 px-5 py-12 lg:grid-cols-[0.9fr_1.1fr] lg:py-16" aria-busy="true">
      <div className="space-y-4">
        <Skeleton className="h-12 w-12" />
        <Skeleton className="h-10 w-4/5" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-3/4" />
      </div>
      <div className="space-y-4 rounded-2xl border border-line bg-surface p-6">
        <span className="sr-only" role="status">Loading the current account-deletion notice</span>
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    </main>
  );
}

function PolicyError({ onRetry }) {
  return (
    <main className="mx-auto grid w-full max-w-xl flex-1 place-items-center px-5 py-20" data-testid="account-deletion-policy-error">
      <div className="text-center" role="alert">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-xl bg-critical-soft text-critical">
          <AlertTriangle className="h-7 w-7" aria-hidden="true" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-ink">Requests are temporarily paused</h1>
        <p className="mt-3 leading-7 text-ink-2">
          We could not verify the current deletion and retention notices. No request has been submitted. Check your connection and try again.
        </p>
        <Button className="mt-6 min-h-11" variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" /> Try again
        </Button>
      </div>
    </main>
  );
}

function StartForm({ policy, initialSchoolCode, onStarted }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, school_code: initialSchoolCode || "" }));
  const [acknowledged, setAcknowledged] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const errorRef = useRef(null);
  const identifierHint = useMemo(() => {
    if (form.persona === "staff") return "Use the email address or mobile number registered with the school.";
    if (form.persona === "parent") return "Use your registered email or mobile number, or your student's admission or registration number.";
    if (form.persona === "student") return "Use your registered email or mobile number, or your admission or registration number.";
    return "The verification code is sent only to the registered contact for the matching account.";
  }, [form.persona]);

  const set = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (error) setError("");
  };

  const valid = isNonEmptyString(form.school_code, 120)
    && PERSONAS.some(({ value }) => value === form.persona)
    && isNonEmptyString(form.identifier, 320)
    && acknowledged;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/public/account-deletion/start", {
        school_code: form.school_code.trim(),
        persona: form.persona,
        identifier: form.identifier.trim(),
        captcha_token: captchaToken || undefined,
        policy_version: policy.policy_version,
        retention_notice_version: policy.retention_notice_version,
        acknowledged: true,
      });
      if (!isNonEmptyString(data?.request_id, 64) || !isNonEmptyString(data?.access_key, 128)) {
        throw new Error("The server did not return a secure request reference.");
      }
      const reference = {
        school_code: form.school_code.trim(),
        request_id: data.request_id,
        access_key: data.access_key,
        resend_available_at: Date.now() + RESEND_COOLDOWN_SECONDS * 1000,
      };
      writeDeletionSession(reference);
      onStarted({ reference, message: data.message, devOtp: data.dev_otp });
    } catch (requestError) {
      const changed = requestError?.response?.status === 409;
      setError(changed
        ? "The deletion notice changed before submission. Reload this page and review the current notice."
        : safeDetail(requestError, "We could not start the request. Check your connection and try again."));
      window.setTimeout(() => errorRef.current?.focus(), 0);
    } finally {
      resetCaptcha();
      setCaptchaToken("");
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="account-deletion-start" aria-busy={busy}>
      <div>
        <h2 className="font-heading text-xl font-semibold text-ink">Verify the account holder</h2>
        <p className="mt-2 text-sm leading-6 text-ink-2">
          Enter the details used with your school. We never confirm whether an account exists on this page.
        </p>
      </div>

      {error ? <p ref={errorRef} tabIndex={-1} role="alert" className="mt-5 rounded-xl bg-critical-soft px-4 py-3 text-sm leading-6 text-critical" data-testid="account-deletion-error">{error}</p> : null}

      <div className="mt-6">
        <Label htmlFor="deletion-school-code">School code</Label>
        <Input
          id="deletion-school-code"
          data-testid="deletion-school-code"
          autoComplete="organization"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={120}
          required
          value={form.school_code}
          onChange={(event) => set("school_code", event.target.value)}
          placeholder="Code provided by your school"
          className="min-h-11"
        />
      </div>

      <fieldset className="mt-5">
        <legend className="label-base">Account type</legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {PERSONAS.map(({ value, label, hint }) => (
            <label key={value} className={`flex min-h-12 cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors focus-within:ring-2 focus-within:ring-focusring/30 ${form.persona === value ? "border-accent bg-accent/10" : "border-line bg-surface hover:bg-muted"}`}>
              <input
                type="radio"
                name="deletion-persona"
                value={value}
                checked={form.persona === value}
                onChange={(event) => set("persona", event.target.value)}
                className="mt-0.5 h-4 w-4 accent-primary"
                data-testid={`deletion-persona-${value}`}
                required
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-ink">{label}</span>
                <span className="mt-0.5 block text-[11px] leading-4 text-ink-subtle">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="mt-5">
        <Label htmlFor="deletion-identifier">Account identifier</Label>
        <Input
          id="deletion-identifier"
          data-testid="deletion-identifier"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={320}
          required
          value={form.identifier}
          onChange={(event) => set("identifier", event.target.value)}
          placeholder="Registered email, mobile or school ID"
          aria-describedby="deletion-identifier-hint"
          className="min-h-11"
        />
        <p id="deletion-identifier-hint" className="mt-2 text-xs leading-5 text-ink-subtle">{identifierHint}</p>
      </div>

      <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-muted px-4 py-4 focus-within:ring-2 focus-within:ring-focusring/30">
        <input
          type="checkbox"
          required
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
          data-testid="deletion-acknowledged"
        />
        <span className="text-sm leading-6 text-ink-2">
          I have read the current deletion and retention notices. I understand this submits a review request and does not erase data immediately.
        </span>
      </label>

      <Captcha onToken={setCaptchaToken} action="account_deletion" className="mt-5 flex justify-center" />

      <Button type="submit" variant="danger" className="mt-6 min-h-12 w-full" loading={busy} disabled={!valid} data-testid="deletion-start-submit">
        <Trash2 className="h-4 w-4" aria-hidden="true" /> Continue to verification
      </Button>
      <p className="mt-3 text-center text-xs leading-5 text-ink-subtle">
        No account or school data is changed at this step.
      </p>
    </form>
  );
}

function VerifyForm({ reference, genericMessage, devOtp, onVerified, onForget }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(genericMessage || "If those details match an account, a verification code has been sent to its registered contact.");
  const [clock, setClock] = useState(Date.now());
  const [activeReference, setActiveReference] = useState(reference);
  const errorRef = useRef(null);
  const remaining = resendSecondsRemaining(activeReference, clock);

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [remaining]);

  const verify = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(otp) || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/public/account-deletion/verify", {
        school_code: activeReference.school_code,
        request_id: activeReference.request_id,
        access_key: activeReference.access_key,
        otp,
      });
      if (!data?.request?.status) throw new Error("The server did not return a request status.");
      onVerified(data.request, data.message);
    } catch (requestError) {
      setError(safeDetail(requestError, "We could not verify the code. Check your connection and try again."));
      window.setTimeout(() => errorRef.current?.focus(), 0);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (remaining > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      const { data } = await api.post("/public/account-deletion/resend", {
        school_code: activeReference.school_code,
        request_id: activeReference.request_id,
        access_key: activeReference.access_key,
      });
      const nextReference = {
        ...activeReference,
        resend_available_at: Date.now() + RESEND_COOLDOWN_SECONDS * 1000,
      };
      writeDeletionSession(nextReference);
      setActiveReference(nextReference);
      setClock(Date.now());
      setNotice(data?.message || "A new verification code has been sent.");
      setOtp("");
    } catch (requestError) {
      setError(safeDetail(requestError, "We could not send another code. Please try again later."));
      if (requestError?.response?.status === 429) {
        const nextReference = {
          ...activeReference,
          resend_available_at: Date.now() + RESEND_COOLDOWN_SECONDS * 1000,
        };
        writeDeletionSession(nextReference);
        setActiveReference(nextReference);
        setClock(Date.now());
      }
      window.setTimeout(() => errorRef.current?.focus(), 0);
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="account-deletion-verify">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-informative-soft text-informative">
        <KeyRound className="h-5 w-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 font-heading text-xl font-semibold text-ink">Enter the verification code</h2>
      <p className="mt-2 text-sm leading-6 text-ink-2" aria-live="polite">{notice}</p>
      <p className="mt-2 text-xs leading-5 text-ink-subtle">For privacy, this message is the same whether or not the details matched an account.</p>

      {process.env.NODE_ENV !== "production" && devOtp ? (
        <p className="mt-4 rounded-xl bg-caution-soft px-4 py-3 text-sm text-caution" data-testid="deletion-dev-otp">
          Development verification code: <strong>{devOtp}</strong>
        </p>
      ) : null}

      {error ? <p ref={errorRef} tabIndex={-1} role="alert" className="mt-5 rounded-xl bg-critical-soft px-4 py-3 text-sm leading-6 text-critical" data-testid="account-deletion-error">{error}</p> : null}

      <form onSubmit={verify} noValidate className="mt-6" aria-busy={busy}>
        <Label htmlFor="deletion-otp">Six-digit code</Label>
        <Input
          id="deletion-otp"
          data-testid="deletion-otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          required
          value={otp}
          onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
          className="min-h-12 text-center font-mono text-xl tracking-[0.28em]"
          autoFocus
        />
        <Button type="submit" className="mt-5 min-h-12 w-full" loading={busy} disabled={!/^\d{6}$/.test(otp)} data-testid="deletion-verify-submit">
          <LockKeyhole className="h-4 w-4" aria-hidden="true" /> Verify and submit request
        </Button>
      </form>

      <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
        <button
          type="button"
          onClick={resend}
          disabled={remaining > 0 || resending}
          className="min-h-11 rounded-lg px-3 text-sm font-medium text-accent-active hover:bg-muted disabled:cursor-not-allowed disabled:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/50"
          data-testid="deletion-resend"
        >
          {resending ? "Sending…" : remaining > 0 ? `Resend in ${remaining}s` : "Resend code"}
        </button>
        <button type="button" onClick={onForget} className="min-h-11 rounded-lg px-3 text-sm text-ink-subtle hover:bg-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/50">
          Clear and start again
        </button>
      </div>
    </div>
  );
}

function StatusPanel({ request, message, error, onRefresh, refreshing, onForget, panelRef }) {
  const presentation = STATUS_COPY[request?.status] || {
    title: "Status unavailable",
    description: "The request returned an unrecognised status. Refresh or contact your school for help.",
    tone: "neutral",
  };
  const tone = {
    positive: "bg-positive-soft text-positive",
    critical: "bg-critical-soft text-critical",
    caution: "bg-caution-soft text-caution",
    informative: "bg-informative-soft text-informative",
    neutral: "bg-muted text-ink-2",
  }[presentation.tone];

  return (
    <div ref={panelRef} data-testid="account-deletion-status" tabIndex={-1} role="region" aria-labelledby="deletion-status-title" className="focus:outline-none">
      <span className={`grid h-12 w-12 place-items-center rounded-xl ${tone}`}>
        {request.status === "completed" ? <CheckCircle2 className="h-6 w-6" aria-hidden="true" /> : <Clock3 className="h-6 w-6" aria-hidden="true" />}
      </span>
      <h2 id="deletion-status-title" className="mt-5 font-heading text-2xl font-semibold text-ink">{presentation.title}</h2>
      <p className="mt-2 text-sm leading-6 text-ink-2"><BrandCopy>{message || presentation.description}</BrandCopy></p>
      {error ? <p role="alert" className="mt-4 rounded-xl bg-critical-soft px-4 py-3 text-sm leading-6 text-critical">{error}</p> : null}

      <dl className="mt-6 divide-y divide-line border-y border-line text-sm">
        <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]"><dt className="text-ink-subtle">Request reference</dt><dd className="break-all font-mono text-xs font-medium text-ink">{request.request_id}</dd></div>
        <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]"><dt className="text-ink-subtle">Account type</dt><dd className="capitalize font-medium text-ink">{request.persona || "Not available"}</dd></div>
        <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]"><dt className="text-ink-subtle">Submitted</dt><dd className="font-medium text-ink">{formatDate(request.submitted_at || request.created_at)}</dd></div>
        {request.decision_reason ? <div className="grid gap-1 py-3 sm:grid-cols-[9rem_1fr]"><dt className="text-ink-subtle">Review outcome</dt><dd className="font-medium text-ink">{request.decision_reason}</dd></div> : null}
      </dl>

      {request.can_cancel ? (
        <p className="mt-5 rounded-xl bg-caution-soft px-4 py-3 text-sm leading-6 text-caution">
          This request can still be cancelled. <Link to={loginPath(request.persona)} className="font-semibold underline underline-offset-4">Sign in to the app</Link> and open your account-deletion settings.
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button variant="outline" onClick={onRefresh} loading={refreshing} className="min-h-11 flex-1" data-testid="deletion-status-refresh">
          <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh status
        </Button>
        <Button variant="ghost" onClick={onForget} className="min-h-11 flex-1">Forget on this device</Button>
      </div>
      <p className="mt-3 text-center text-xs leading-5 text-ink-subtle">Forgetting removes only this browser-tab reference. It does not cancel the request.</p>
    </div>
  );
}

export default function AccountDeletion() {
  const [policyState, setPolicyState] = useState({ status: "loading", policy: null });
  const [stage, setStage] = useState("start");
  const [reference, setReference] = useState(null);
  const [request, setRequest] = useState(null);
  const [message, setMessage] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const [recoveryNotice, setRecoveryNotice] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const statusRef = useRef(null);
  const initialSchoolCode = reference?.school_code || getTenantSlug() || "";

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    let alive = true;
    setPolicyState({ status: "loading", policy: null });
    setRecoveryNotice("");
    (async () => {
      try {
        const { data } = await api.get("/public/account-deletion/policy");
        if (!hasCompleteDeletionPolicy(data?.policy)) throw new Error("Incomplete policy contract.");
        if (!alive) return;
        setPolicyState({ status: "ready", policy: data.policy });

        const saved = readDeletionSession();
        if (!saved) return;
        setReference(saved);
        try {
          const statusResponse = await api.post("/public/account-deletion/status", {
            school_code: saved.school_code,
            request_id: saved.request_id,
            access_key: saved.access_key,
          });
          if (!alive || !statusResponse.data?.request?.status) return;
          setRequest(statusResponse.data.request);
          setStage(requestStage(statusResponse.data.request));
        } catch {
          if (!alive) return;
          clearDeletionSession();
          setReference(null);
          setRequest(null);
          setStage("start");
          setRecoveryNotice("The saved request could not be restored. For your privacy, its local reference was cleared; you can start again.");
        }
      } catch {
        if (alive) setPolicyState({ status: "error", policy: null });
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const forget = () => {
    clearDeletionSession();
    setReference(null);
    setRequest(null);
    setMessage("");
    setDevOtp("");
    setRefreshError("");
    setStage("start");
  };

  const refreshStatus = async () => {
    if (!reference || refreshing) return;
    setRefreshing(true);
    setRefreshError("");
    try {
      const { data } = await api.post("/public/account-deletion/status", {
        school_code: reference.school_code,
        request_id: reference.request_id,
        access_key: reference.access_key,
      });
      if (!data?.request?.status) throw new Error("Missing request status.");
      setRequest(data.request);
      setStage(requestStage(data.request));
      setMessage("");
      window.setTimeout(() => statusRef.current?.focus(), 0);
    } catch (error) {
      setRefreshError(safeDetail(error, "We could not refresh the status. Check your connection and try again."));
    } finally {
      setRefreshing(false);
    }
  };

  const policy = policyState.policy;
  return (
    <div className="flex min-h-screen flex-col bg-canvas text-ink">
      <MarketingSeo
        title="Request Account Deletion | Vidyapeeth360"
        description="Submit and verify a Vidyapeeth360 account-deletion request, then check its review and processing status."
        path="/account-deletion"
        robots="noindex, nofollow, noarchive"
      />
      {/* WP-07: the ONE marketing header (was a fourth bespoke variant with its own
          "Back to website" affordance — the logo is the way home). */}
      <MarketingTopBar />

      {policyState.status === "loading" ? <PolicyLoading /> : null}
      {policyState.status === "error" ? <PolicyError onRetry={() => setReloadKey((value) => value + 1)} /> : null}

      {policyState.status === "ready" && policy ? (
        <main className="mx-auto grid w-full max-w-6xl flex-1 gap-10 px-5 py-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:py-16">
          <PolicyPanel policy={policy} />
          <section aria-label="Account-deletion request" className="self-start rounded-2xl border border-line bg-surface p-5 shadow-card sm:p-7">
            <Progress stage={stage} />
            {recoveryNotice ? <p role="status" className="mt-6 rounded-xl bg-caution-soft px-4 py-3 text-sm leading-6 text-caution">{recoveryNotice}</p> : null}
            <div className="mt-7">
              {stage === "start" ? (
                <StartForm
                  key={initialSchoolCode}
                  policy={policy}
                  initialSchoolCode={initialSchoolCode}
                  onStarted={({ reference: next, message: nextMessage, devOtp: nextDevOtp }) => {
                    setReference(next);
                    setMessage(nextMessage || "");
                    setDevOtp(nextDevOtp || "");
                    setStage("verify");
                  }}
                />
              ) : null}
              {stage === "verify" && reference ? (
                <VerifyForm
                  reference={reference}
                  genericMessage={message}
                  devOtp={devOtp}
                  onForget={forget}
                  onVerified={(nextRequest, nextMessage) => {
                    setRequest(nextRequest);
                    setMessage(nextMessage || "");
                    setRefreshError("");
                    setStage(requestStage(nextRequest));
                    window.setTimeout(() => statusRef.current?.focus(), 0);
                  }}
                />
              ) : null}
              {stage === "status" && request ? (
                <StatusPanel request={request} message={message} error={refreshError} onRefresh={refreshStatus} refreshing={refreshing} onForget={forget} panelRef={statusRef} />
              ) : null}
            </div>
          </section>
        </main>
      ) : null}

      <SiteFooter />
    </div>
  );
}
